// validators/number.ts
import { assertSchema, assertValidation, ValidationError } from "../errors.mjs";
import { AbstractValidator, makeValidatorFactory } from "../types.mjs";
var PRECISION = 6;
var MULTIPLIER = Math.pow(10, PRECISION);
function countDecimals(n) {
  const match = n.toExponential().match(/^\d+(\.\d+)?e([+-]\d+)$/);
  if (!match) throw new RangeError(`Can't calculate digits for number "${n}"`);
  const digits = (match[1] || ".").length - 1 - parseInt(match[2]);
  return digits < 0 ? 0 : digits;
}
var AnyNumberValidator = class extends AbstractValidator {
  validate(value) {
    assertValidation(typeof value === "number", 'Value is not a "number"');
    assertValidation(!isNaN(value), 'Number is "NaN"');
    return value;
  }
};
var NumberValidator = class extends AbstractValidator {
  #isMultipleOf;
  allowNaN;
  exclusiveMaximum;
  exclusiveMinimum;
  fromString;
  maximum;
  minimum;
  multipleOf;
  brand;
  constructor(constraints = {}) {
    super();
    const {
      allowNaN = false,
      exclusiveMaximum,
      exclusiveMinimum,
      fromString = false,
      maximum = Number.POSITIVE_INFINITY,
      minimum = Number.NEGATIVE_INFINITY,
      multipleOf
    } = constraints;
    if ("brand" in constraints) this.brand = constraints.brand;
    assertSchema(maximum >= minimum, `Constraint "minimum" (${minimum}) is greater than "maximum" (${maximum})`);
    if (exclusiveMaximum !== void 0) {
      assertSchema(
        exclusiveMaximum > minimum,
        `Constraint "exclusiveMaximum" (${exclusiveMaximum}) must be greater than "minimum" (${minimum})`
      );
    }
    if (exclusiveMinimum !== void 0) {
      assertSchema(
        maximum > exclusiveMinimum,
        `Constraint "maximum" (${maximum}) must be greater than "exclusiveMinimum" (${exclusiveMinimum})`
      );
    }
    if (exclusiveMinimum != void 0 && exclusiveMaximum !== void 0) {
      assertSchema(
        exclusiveMaximum > exclusiveMinimum,
        `Constraint "exclusiveMaximum" (${exclusiveMaximum}) must be greater than "exclusiveMinimum" (${exclusiveMinimum})`
      );
    }
    if (multipleOf !== void 0) {
      assertSchema(multipleOf > 0, `Constraint "multipleOf" (${multipleOf}) must be greater than zero`);
      const decimals = countDecimals(multipleOf);
      if (decimals === 0) {
        this.#isMultipleOf = (value) => !(value % multipleOf);
      } else if (decimals <= PRECISION) {
        this.#isMultipleOf = (value) => {
          try {
            if (countDecimals(value) > PRECISION) return false;
            return !(value * MULTIPLIER % (multipleOf * MULTIPLIER));
          } catch (error) {
            throw new ValidationError(error.message);
          }
        };
      } else {
        assertSchema(false, `Constraint "multipleOf" (${multipleOf}) requires too much precision`);
      }
    }
    this.allowNaN = allowNaN;
    this.exclusiveMaximum = exclusiveMaximum;
    this.exclusiveMinimum = exclusiveMinimum;
    this.fromString = fromString;
    this.maximum = maximum;
    this.minimum = minimum;
    this.multipleOf = multipleOf;
  }
  validate(value) {
    if (typeof value === "string" && this.fromString) {
      const parsed = +`${value}`;
      assertValidation(!isNaN(parsed), "Number can not be parsed from string");
      value = parsed;
    }
    assertValidation(typeof value === "number", 'Value is not a "number"');
    if (isNaN(value)) {
      assertValidation(this.allowNaN, 'Number is "NaN"');
      return value;
    }
    assertValidation(value >= this.minimum, `Number is less than ${this.minimum}`);
    assertValidation(value <= this.maximum, `Number is greater than ${this.maximum}`);
    assertValidation(
      this.exclusiveMinimum === void 0 || value > this.exclusiveMinimum,
      `Number is less than or equal to ${this.exclusiveMinimum}`
    );
    assertValidation(
      this.exclusiveMaximum === void 0 || value < this.exclusiveMaximum,
      `Number is greater than or equal to ${this.exclusiveMaximum}`
    );
    assertValidation(
      this.#isMultipleOf ? this.#isMultipleOf(value) : true,
      `Number is not a multiple of ${this.multipleOf}`
    );
    return value;
  }
};
function numberValidatorFactory(constraints) {
  return new NumberValidator(constraints);
}
var number = makeValidatorFactory(new AnyNumberValidator(), numberValidatorFactory);
export {
  AnyNumberValidator,
  NumberValidator,
  number,
  numberValidatorFactory
};
//# sourceMappingURL=number.mjs.map
